// Copilot confirm modal for write tool calls — supports a batch of N actions (SPEC 001 T144)
import React, { useState } from "react";
import { CopilotToolCallPreviewViewModel } from "../../interfaces/Copilot";
import { cancelBatch, confirmBatch } from "../../services/copilot/copilotApi";
import { CopilotTurnResponse } from "../../interfaces/Copilot";

interface CopilotConfirmModalProps {
  toolCalls: CopilotToolCallPreviewViewModel[];
  onConfirmed: (turn: CopilotTurnResponse) => void;
  onCancelled: (turn: CopilotTurnResponse) => void;
  onClose: () => void;
}

const CopilotConfirmModal: React.FC<CopilotConfirmModalProps> = ({
  toolCalls,
  onConfirmed,
  onCancelled,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const batchId = toolCalls[0]?.batchId;
  const multiple = toolCalls.length > 1;

  const handleConfirm = async () => {
    if (!batchId) return;
    setLoading(true);
    setError(null);
    try {
      const turn = await confirmBatch(batchId);
      onConfirmed(turn);
    } catch (err: any) {
      const status = err?.response?.status;
      const code = err?.response?.data?.code;
      const serverMessage = err?.response?.data?.message;
      const serverTurn: CopilotTurnResponse | undefined = err?.response?.data?.turn;

      if (status === 409 && code === "ToolCallNotPending") {
        setError("Esta ação já foi processada ou expirou. Atualize a conversa.");
      } else if (status === 422 && code === "IntegrationFailure") {
        if (serverTurn) {
          onConfirmed(serverTurn);
        } else {
          setError(serverMessage || "Falha ao executar a ação. Tente novamente.");
        }
      } else if (status === 403 && code === "PermissionDenied") {
        setError("Você não tem permissão para confirmar esta ação.");
      } else {
        setError(
          serverMessage ||
          err?.response?.data?.title ||
          "Erro ao confirmar a ação. Tente novamente."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!batchId) return;
    setLoading(true);
    setError(null);
    try {
      const turn = await cancelBatch(batchId);
      onCancelled(turn);
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        "Erro ao cancelar a ação.";
      setError(msg);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/60 p-4 backdrop-blur-sm">
      <div className="max-h-[85vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-5 nina-surface shadow-dv-lg dark:bg-navy-800">
        <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-1">
          {multiple ? `Confirmar ${toolCalls.length} ações` : "Confirmar ação"}
        </h2>

        {toolCalls.map((toolCall, tcIdx) => (
          <div key={toolCall.id} className="mb-4">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
              {multiple && (
                <span className="font-semibold mr-1">{tcIdx + 1}.</span>
              )}
              {toolCall.preview.summaryMarkdown}
            </p>

            <div className="rounded-lg border border-gray-200 dark:border-navy-600 divide-y divide-gray-200 dark:divide-navy-600">
              {toolCall.preview.fields.map((field, idx) => (
                <div key={idx} className="flex px-3 py-2 text-sm">
                  <span className="w-1/3 font-medium text-gray-600 dark:text-gray-300 shrink-0">
                    {field.label}
                  </span>
                  <span className="text-gray-900 dark:text-white break-words">
                    {field.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}

        {error && <p className="text-sm text-red-500 mb-3">{error}</p>}

        <div className="flex gap-2 justify-end">
          <button
            onClick={handleCancel}
            disabled={loading}
            className="px-4 py-2 rounded-lg border border-gray-300 dark:border-navy-500 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-navy-700 disabled:opacity-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading}
            className="dv-sheen rounded-lg bg-dv-gradient px-4 py-2 text-sm font-semibold text-white shadow-dv-glow transition-all active:scale-[.98] disabled:opacity-50"
          >
            {loading ? "Processando..." : multiple ? "Confirmar tudo" : "Confirmar"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CopilotConfirmModal;
